import { RTU } from '../../types/RTU';

export type UsageComparison = '>' | '<';

export interface UsageCriterion {
  comparison: UsageComparison;
  value: number | string | null;
}

export interface RtuFilterCriteria {
  status?: string | null;
  cpuUsage?: UsageCriterion | null;
  diskUsage?: UsageCriterion | null;
  location?: string | null;
  producer?: string | null;
  field?: string | null;
}

export const EMPTY_CRITERIA: RtuFilterCriteria = {};

const normalizeTerm = (term: string | null | undefined): string =>
  typeof term === 'string' ? term.trim().toLowerCase() : '';

const normalizeStatus = (status: string | null | undefined): string | null => {
  if (typeof status !== 'string') return null;
  const trimmed = status.trim();
  return trimmed === '' || trimmed.toLowerCase() === 'all' ? null : status;
};

const normalizeUsage = (
  criterion: UsageCriterion | null | undefined
): { comparison: UsageComparison; value: number } | null => {
  if (!criterion) return null;
  if (criterion.comparison !== '>' && criterion.comparison !== '<') return null;
  const raw = criterion.value;
  let value: number;
  if (typeof raw === 'number') {
    value = raw;
  } else if (typeof raw === 'string' && raw.trim() !== '') {
    value = Number(raw);
  } else {
    return null;
  }
  return Number.isFinite(value) ? { comparison: criterion.comparison, value } : null;
};

const containsTerm = (rtuValue: string | null | undefined, term: string): boolean =>
  typeof rtuValue === 'string' && rtuValue.toLowerCase().includes(term);

const matchesUsage = (
  actual: number | null | undefined,
  usage: { comparison: UsageComparison; value: number }
): boolean => {
  if (typeof actual !== 'number' || !Number.isFinite(actual)) return false;
  return usage.comparison === '>' ? actual > usage.value : actual < usage.value;
};

export const isCriteriaActive = (criteria: RtuFilterCriteria | null | undefined): boolean => {
  if (!criteria) return false;
  return (
    normalizeStatus(criteria.status) !== null ||
    normalizeUsage(criteria.cpuUsage) !== null ||
    normalizeUsage(criteria.diskUsage) !== null ||
    normalizeTerm(criteria.location) !== '' ||
    normalizeTerm(criteria.producer) !== '' ||
    normalizeTerm(criteria.field) !== ''
  );
};

export const filterRtus = (rtus: RTU[] | null | undefined, criteria: RtuFilterCriteria | null | undefined): RTU[] => {
  const list = rtus || [];
  if (!isCriteriaActive(criteria)) return list;
  const c = criteria as RtuFilterCriteria;
  const status = normalizeStatus(c.status);
  const cpu = normalizeUsage(c.cpuUsage);
  const disk = normalizeUsage(c.diskUsage);
  const location = normalizeTerm(c.location);
  const producer = normalizeTerm(c.producer);
  const field = normalizeTerm(c.field);

  return list.filter(
    (rtu) =>
      (status === null || rtu.status === status) &&
      (cpu === null || matchesUsage(rtu.cpuUsage, cpu)) &&
      (disk === null || matchesUsage(rtu.diskUsage, disk)) &&
      (location === '' || containsTerm(rtu.location, location)) &&
      (producer === '' || containsTerm(rtu.provider, producer)) &&
      (field === '' || containsTerm(rtu.field, field))
  );
};
